import {
    Hero,
    HeroText,
    Title,
    SubTitle,
    Description,
    StoreBadge,
    PhoneFrame,
    Section,
    SectionTitle,
    Steps,
    StepCard,
    StepNumber,
    StepTitle,
    StepDescription,
    ScreenshotStrip,
    ScreenshotCard,
    ScreenshotFrame,
    ScreenshotCaption,
    FeatureList,
    FeatureItem,
    FeatureIcon,
    FeatureText,
} from './styles';
import { useTheme } from '../../hooks/useTheme';
import { appStoreURL } from '../../constants/urls';
import { screenshots } from '../../constants/screenshots';
import Screenshot from '../Screenshot';

const steps = [
    {
        title: 'Describe anything',
        description: 'Type a simple idea — “a dinosaur astronaut,” “a unicorn at a birthday party,” anything at all.',
    },
    {
        title: 'Pick a detail level',
        description: 'From big, simple shapes for toddlers to intricate scenes for older kids and grown-ups — five levels in all.',
    },
    {
        title: 'Print and color',
        description: 'Get a ready-to-print coloring page in moments. Print it out, grab your crayons, and start coloring.',
    },
];

const features = [
    {
        icon: '🛡️',
        title: 'Kid-friendly by design',
        text: 'Every request is checked, and the community gallery is reviewed by a person before anything is shown.',
    },
    {
        icon: '🎚️',
        title: 'Five detail levels',
        text: 'From toddlers to adults, there’s a level with just the right amount of detail.',
    },
    {
        icon: '🖼️',
        title: 'Community gallery',
        text: 'Browse coloring pages other families have made and shared.',
    },
    {
        icon: '🖨️',
        title: 'Print or share',
        text: 'Print your page straight from the app, or share it with family.',
    },
    {
        icon: '✨',
        title: 'Free drawings to start',
        text: 'Try Magic Crayons before you decide to subscribe.',
    },
    {
        icon: '💗',
        title: 'Premium for unlimited drawings',
        text: 'Make as many coloring pages as your family wants (fair use applies).',
    },
];

const Home = () => {
    const isDarkMode = useTheme() === 'dark';
    const [heroShot, ...stripShots] = screenshots;

    return (
        <>
            <Hero>
                <HeroText>
                    <Title>Magic Crayons</Title>
                    <SubTitle>Coloring pages from your imagination.</SubTitle>
                    <Description>
                        Type any idea and Magic Crayons turns it into a printable coloring page made just for your
                        kid. Pick from five detail levels, from big simple shapes for toddlers to intricate scenes
                        for older kids and grown-ups, then print it out and start coloring together.
                    </Description>
                    <a href={appStoreURL}>
                        <StoreBadge src="/assets/Download_on_the_App_Store_Badge.svg" alt="Download on the App Store" />
                    </a>
                </HeroText>
                {heroShot && (
                    <PhoneFrame>
                        <Screenshot filename={heroShot.filename} alt={heroShot.alt} dark={isDarkMode} />
                    </PhoneFrame>
                )}
            </Hero>

            <Section>
                <SectionTitle>How it works</SectionTitle>
                <Steps>
                    {steps.map((step, index) => (
                        <StepCard key={step.title}>
                            <StepNumber>{index + 1}</StepNumber>
                            <StepTitle>{step.title}</StepTitle>
                            <StepDescription>{step.description}</StepDescription>
                        </StepCard>
                    ))}
                </Steps>
            </Section>

            {stripShots.length > 0 && (
                <Section>
                    <SectionTitle>See it in action</SectionTitle>
                    <ScreenshotStrip>
                        {stripShots.map((shot) => (
                            <ScreenshotCard key={shot.filename}>
                                <ScreenshotFrame>
                                    <Screenshot filename={shot.filename} alt={shot.alt} dark={isDarkMode} />
                                </ScreenshotFrame>
                                <ScreenshotCaption>{shot.caption}</ScreenshotCaption>
                            </ScreenshotCard>
                        ))}
                    </ScreenshotStrip>
                </Section>
            )}

            <Section>
                <SectionTitle>Why parents like it</SectionTitle>
                <FeatureList>
                    {features.map((feature) => (
                        <FeatureItem key={feature.title}>
                            <FeatureIcon aria-hidden="true">{feature.icon}</FeatureIcon>
                            <FeatureText>
                                <strong>{feature.title}.</strong> {feature.text}
                            </FeatureText>
                        </FeatureItem>
                    ))}
                </FeatureList>
            </Section>
        </>
    );
}

export default Home;
